import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import MoyasarForm from '../components/MoyasarForm';
import { Upload, FileText, Loader2, Stethoscope, ShieldCheck, Lock, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GuestAnalyzePage() {
    const { i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [status, setStatus] = useState(null);
    const [gender, setGender] = useState('');
    const [age, setAge] = useState('');
    const [files, setFiles] = useState([]);
    const [analyzing, setAnalyzing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [limitReached, setLimitReached] = useState(false);
    const [paymentConfig, setPaymentConfig] = useState(null);
    const [payOpen, setPayOpen] = useState(false);
    const [preparingPay, setPreparingPay] = useState(false);
    const fileRef = useRef(null);
    const verifiedRef = useRef(false);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/guest/status`);
            setStatus(res.data);
        } catch (e) { /* ignore */ }
    }, []);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    // Handle Moyasar redirect back (?transaction_id=..&id=..&status=..)
    useEffect(() => {
        const txnId = searchParams.get('transaction_id');
        const paymentId = searchParams.get('id');
        if (txnId && paymentId && !verifiedRef.current) {
            verifiedRef.current = true;
            axios.get(`${API}/guest/payments/verify`, { params: { payment_id: paymentId, transaction_id: txnId } })
                .then((res) => {
                    if (res.data.status === 'success') {
                        toast.success(isRTL ? 'تم الدفع بنجاح — ارفع تقريرك الآن' : 'Payment successful — upload your report now');
                        setLimitReached(false);
                    } else {
                        toast.error(isRTL ? 'لم يكتمل الدفع' : 'Payment was not completed');
                    }
                })
                .catch(() => toast.error(isRTL ? 'تعذر التحقق من الدفع' : 'Payment verification failed'))
                .finally(() => { setSearchParams({}, { replace: true }); fetchStatus(); });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleAnalyze = async () => {
        if (!files.length) { toast.error(isRTL ? 'اختر ملفات التقرير أولاً' : 'Choose report file(s) first'); return; }
        if (!gender || !age) { toast.error(isRTL ? 'أدخل الجنس والعمر' : 'Enter gender and age'); return; }
        setAnalyzing(true);
        setProgress(8);
        const timer = setInterval(() => setProgress((p) => Math.min(p + Math.random() * 9, 92)), 900);
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append('files', f));
            fd.append('patient_gender', gender);
            fd.append('patient_age', age);
            fd.append('language', isRTL ? 'ar' : 'en');
            const res = await axios.post(`${API}/guest/analyze`, fd);
            const shareToken = res.data.share_token;
            let st = res.data.status || 'completed';
            // Poll the public shared endpoint until the background analysis completes
            while (st === 'processing') {
                await new Promise((r) => setTimeout(r, 4000));
                const check = await axios.get(`${API}/shared/${shareToken}`);
                st = check.data.status || 'completed';
            }
            if (st === 'failed') {
                toast.error(isRTL ? 'تعذر تحليل التقرير — لم يُخصم استخدامك المجاني، حاول مرة أخرى' : 'Analysis failed — your free use was not consumed, try again');
                return;
            }
            setProgress(100);
            toast.success(isRTL ? 'تم تحليل تقريرك بنجاح!' : 'Your report was analyzed!');
            navigate(`/shared/${shareToken}`);
        } catch (e) {
            if (e.response?.status === 402) {
                setLimitReached(true);
            } else {
                toast.error(e.response?.data?.detail || (isRTL ? 'تعذر تحليل التقرير' : 'Analysis failed'));
            }
        } finally {
            clearInterval(timer);
            setAnalyzing(false);
            setProgress(0);
            fetchStatus();
        }
    };

    const handleGuestPay = async () => {
        setPreparingPay(true);
        try {
            const res = await axios.post(`${API}/guest/payments/create`);
            setPaymentConfig({
                publishable_key: res.data.publishable_key,
                amount: res.data.amount,
                currency: res.data.currency,
                description: res.data.description,
                metadata: res.data.metadata,
                callback_url: `${window.location.origin}/try?transaction_id=${res.data.transaction_id}`,
            });
            setPayOpen(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || (isRTL ? 'تعذر تجهيز الدفع' : 'Failed to prepare payment'));
        } finally {
            setPreparingPay(false);
        }
    };

    return (
        <div dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-stone-50" data-testid="guest-analyze-page">
            <header className="bg-white border-b border-stone-200">
                <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Stethoscope className="w-6 h-6 text-teal-600" />
                        <span className="font-bold text-stone-800 text-lg">{isRTL ? 'نتيجتي الطبية' : 'Natijaty'}</span>
                    </Link>
                    <Link to="/register" className="text-sm text-teal-600 hover:underline" data-testid="guest-register-link">
                        {isRTL ? 'إنشاء حساب' : 'Create account'}
                    </Link>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 py-10">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-stone-800 mb-2">
                        {isRTL ? 'حلّل تقريرك الآن — بدون تسجيل' : 'Analyze your report now — no signup'}
                    </h1>
                    <p className="text-stone-500">
                        {isRTL
                            ? 'خصوصية كاملة: لا إيميل ولا رقم جوال — فقط الجنس والعمر لدقة التحليل'
                            : 'Full privacy: no email, no phone — only gender and age for accurate analysis'}
                    </p>
                    {status && status.free_remaining > 0 && (
                        <span className="inline-flex items-center gap-1.5 mt-3 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-4 py-1.5" data-testid="guest-free-badge">
                            <Sparkles className="w-4 h-4" />
                            {isRTL ? `لديك ${status.free_remaining} تحليل مجاني` : `You have ${status.free_remaining} free analysis`}
                        </span>
                    )}
                    {status && status.paid_credits > 0 && (
                        <span className="inline-flex items-center gap-1.5 mt-3 mx-2 text-sm bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-4 py-1.5" data-testid="guest-paid-badge">
                            {isRTL ? `رصيد مدفوع: ${status.paid_credits}` : `Paid credits: ${status.paid_credits}`}
                        </span>
                    )}
                </div>

                {status && !status.enabled ? (
                    <Card className="border-stone-200"><CardContent className="p-8 text-center text-stone-500">
                        {isRTL ? 'خدمة التحليل للزوار غير متاحة حالياً — يمكنك إنشاء حساب مجاني' : 'Guest analysis is currently unavailable — you can create a free account'}
                    </CardContent></Card>
                ) : limitReached ? (
                    <Card className="border-amber-200 bg-amber-50/50" data-testid="guest-paywall">
                        <CardContent className="p-8 text-center space-y-4">
                            <Lock className="w-10 h-10 text-amber-500 mx-auto" />
                            <h2 className="text-lg font-bold text-stone-800">
                                {isRTL ? 'استنفدت تحليلك المجاني' : 'Free analysis used up'}
                            </h2>
                            <p className="text-sm text-stone-600">
                                {isRTL
                                    ? `يمكنك متابعة التحليل كزائر مقابل ${status?.price_per_report ?? ''} ر.س للتقرير، أو أنشئ حساباً مجانياً واحصل على مزايا الحفظ والمتابعة`
                                    : `Continue as guest for ${status?.price_per_report ?? ''} SAR per report, or create a free account`}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Button onClick={handleGuestPay} disabled={preparingPay} className="bg-teal-500 hover:bg-teal-600 rounded-full px-6" data-testid="guest-pay-btn">
                                    {preparingPay ? <Loader2 className="w-4 h-4 animate-spin" /> : (isRTL ? `ادفع ${status?.price_per_report} ر.س كزائر` : `Pay ${status?.price_per_report} SAR as guest`)}
                                </Button>
                                <Link to="/register">
                                    <Button variant="outline" className="rounded-full px-6 border-teal-300 text-teal-700 w-full" data-testid="guest-paywall-register-btn">
                                        {isRTL ? 'إنشاء حساب مجاني' : 'Create free account'}
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <Card className="border-stone-200 shadow-lg shadow-stone-200/50">
                        <CardContent className="p-6 space-y-5">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>{isRTL ? 'الجنس' : 'Gender'}</Label>
                                    <Select value={gender} onValueChange={setGender}>
                                        <SelectTrigger data-testid="guest-gender-select">
                                            <SelectValue placeholder={isRTL ? 'اختر' : 'Select'} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="male">{isRTL ? 'ذكر' : 'Male'}</SelectItem>
                                            <SelectItem value="female">{isRTL ? 'أنثى' : 'Female'}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>{isRTL ? 'العمر' : 'Age'}</Label>
                                    <Input type="number" min="1" max="120" value={age} onChange={(e) => setAge(e.target.value)}
                                        placeholder={isRTL ? 'مثال: 35' : 'e.g. 35'} data-testid="guest-age-input" />
                                </div>
                            </div>

                            <div
                                onClick={() => fileRef.current?.click()}
                                className="border-2 border-dashed border-stone-300 hover:border-teal-400 rounded-2xl p-8 text-center cursor-pointer transition-colors bg-stone-50/50"
                                data-testid="guest-file-drop"
                            >
                                <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                                    onChange={(e) => {
                                        const incoming = Array.from(e.target.files || []);
                                        setFiles((prev) => [...prev, ...incoming].slice(0, 10));
                                        e.target.value = '';
                                    }} data-testid="guest-file-input" />
                                {files.length ? (
                                    <div className="space-y-1.5" data-testid="guest-files-queue">
                                        {files.map((f, i) => (
                                            <div key={i} className="flex items-center justify-between gap-2 text-teal-700 bg-white rounded-lg px-3 py-1.5 border border-teal-100" data-testid={`guest-file-item-${i}`}>
                                                <span className="flex items-center gap-2 min-w-0">
                                                    <FileText className="w-4 h-4 shrink-0" />
                                                    <span className="text-sm font-medium truncate">{f.name}</span>
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); setFiles((prev) => prev.filter((_, x) => x !== i)); }}
                                                    className="text-stone-400 hover:text-red-500 shrink-0"
                                                    data-testid={`guest-remove-file-${i}`}
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                        <p className="text-xs text-stone-400 text-center pt-1">
                                            {files.length >= 10
                                                ? (isRTL ? 'الحد الأقصى 10 ملفات' : 'Maximum 10 files')
                                                : (isRTL ? `اضغط لإضافة المزيد (${files.length}/10)` : `Click to add more (${files.length}/10)`)}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                                        <p className="text-sm text-stone-600">{isRTL ? 'اضغط لاختيار ملفات التقرير (PDF أو صور — حتى 10 ملفات)' : 'Click to choose report files (PDF or images — up to 10)'}</p>
                                        <p className="text-xs text-stone-400 mt-1">{isRTL ? 'تُحلّل الملفات معاً كتقرير واحد — حتى 10 ميجابايت للملف' : 'Files are analyzed together as one report — up to 10MB each'}</p>
                                    </>
                                )}
                            </div>

                            {analyzing && (
                                <div className="space-y-2">
                                    <Progress value={progress} className="h-2" />
                                    <p className="text-xs text-stone-500 text-center">{isRTL ? 'جاري تحليل تقريرك بالذكاء الاصطناعي...' : 'Analyzing your report with AI...'}</p>
                                </div>
                            )}

                            <Button onClick={handleAnalyze} disabled={analyzing} className="w-full bg-teal-500 hover:bg-teal-600 rounded-full py-6 text-base" data-testid="guest-analyze-btn">
                                {analyzing ? <Loader2 className="w-5 h-5 animate-spin" /> : (isRTL ? 'حلّل التقرير الآن' : 'Analyze now')}
                            </Button>

                            <div className="flex items-center justify-center gap-2 text-xs text-stone-400">
                                <ShieldCheck className="w-4 h-4" />
                                {isRTL ? 'نتيجتك متاحة عبر رابط آمن لمدة 7 أيام ولا تُربط بأي هوية' : 'Your result is available via a secure link for 7 days, not linked to any identity'}
                            </div>
                        </CardContent>
                    </Card>
                )}

                <p className="text-center text-sm text-stone-500 mt-6">
                    {isRTL ? 'تريد حفظ تقاريرك ومتابعة مؤشراتك مع الوقت؟' : 'Want to save reports and track trends over time?'}{' '}
                    <Link to="/register" className="text-teal-600 font-medium hover:underline">{isRTL ? 'سجّل مجاناً' : 'Register free'}</Link>
                </p>
            </main>

            <Dialog open={payOpen} onOpenChange={setPayOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="guest-payment-dialog">
                    <DialogHeader>
                        <DialogTitle>{isRTL ? 'الدفع كزائر' : 'Pay as guest'}</DialogTitle>
                        <DialogDescription>
                            {isRTL ? 'دفع آمن عبر Moyasar — تحليل تقرير واحد' : 'Secure payment via Moyasar — one report analysis'}
                        </DialogDescription>
                    </DialogHeader>
                    {paymentConfig && <MoyasarForm key={paymentConfig.metadata?.transaction_id} config={paymentConfig} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}
